import Bugsnag, { Event } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { CONFIG } from '@/constants/config';
import { isJestRunning } from '@/utils/helpers';

type IgnoredErrorRule = {
  errorClass?: string | string[];
  message: string | RegExp;
};

const IGNORED_ERRORS: IgnoredErrorRule[] = [
  {
    errorClass: 'TypeError',
    message: "Cannot read properties of null (reading 'setItem')",
  },
  {
    errorClass: 'TypeError',
    message: "Cannot read property 'setItem' of null",
  },
  // zustand persist reading localStorage when it's unavailable
  // (private mode, blocked storage, webview): getItem on null.
  {
    errorClass: 'TypeError',
    message: /getItem/,
  },
  // Transient network failures (offline, aborted request, navigation away (mid-request)
  {
    errorClass: 'ApolloError',
    message: /Failed to fetch|Load failed/,
  },
  // PubNub transient statuses (timeout / network issues): pure noise.
  {
    message: /PNTimeoutCategory|PNNetworkIssuesCategory/,
  },
];

const NOOP_BUGSNAG_API_KEY = 'DO_NOT_SEND';

const STAGING_DOMAINS = [
  '.azstaging.net',
  '.azstage.in',
  '.digit-az.app',
  '.vercel.app',
];

const isBrowser = typeof window !== 'undefined';
const apiKey = CONFIG.bugsnagApiKey;

const noopBugsnag = {
  notify: () => undefined,
  isStarted: () => false,
};

function getErrorClasses(event: Event): string[] {
  const classes = (event.errors || []).map(e => e.errorClass).filter(Boolean);

  if (event.originalError instanceof Error && event.originalError.name) {
    classes.push(event.originalError.name);
  }

  return classes;
}

function errorClassMatches(
  allowed: string | string[] | undefined,
  event: Event
): boolean {
  if (!allowed) return true;

  const classes = Array.isArray(allowed) ? allowed : [allowed];
  return getErrorClasses(event).some(c => classes.includes(c));
}

function getErrorMessages(event: Event): string[] {
  return [
    ...(event.errors || []).map(e => e.errorMessage || ''),
    event.originalError instanceof Error ? event.originalError.message : '',
  ].filter(Boolean);
}

function messageMatches(rule: string | RegExp, msg: string): boolean {
  if (rule instanceof RegExp) {
    return rule.test(msg);
  }

  return msg === rule;
}

function shouldIgnoreError(event: Event): boolean {
  const messages = getErrorMessages(event);
  return IGNORED_ERRORS.some(
    rule =>
      errorClassMatches(rule.errorClass, event) &&
      messages.some(msg => messageMatches(rule.message, msg))
  );
}

function matchesStagingDomain(url?: string): boolean {
  return STAGING_DOMAINS.some(domain => url?.includes(domain));
}

function getReleaseStage(): string {
  if (isBrowser) {
    const { hostname } = window.location;

    if (hostname.match(/^(?:.+\.)?tap\.az$/)) {
      return 'production';
    }

    if (matchesStagingDomain(hostname)) {
      return process.env.VERCEL_GITHUB_BRANCH || 'staging';
    }

    if (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0'
    ) {
      return 'local';
    }

    return 'local';
  }

  if (process.env.VERCEL_ENV === 'production') {
    return 'production';
  }

  if (matchesStagingDomain(process.env.VERCEL_URL)) {
    return (
      process.env.VERCEL_GITHUB_BRANCH ||
      process.env.VERCEL_GIT_COMMIT_REF ||
      'staging'
    );
  }

  if (process.env.VERCEL_GITHUB_BRANCH) {
    return process.env.VERCEL_GITHUB_BRANCH;
  }

  return 'local';
}

function isLocalLogging(): boolean {
  if (process.env.E2E_TEST_MODE === 'true') {
    return false;
  }

  return getReleaseStage() === 'local';
}

function getLogContext(): string {
  return isBrowser ? 'Browser' : 'Server';
}

function getEventLogPayload(event: Event) {
  return {
    errorClass: event.errors?.[0]?.errorClass,
    error: event.errors?.[0]?.errorMessage,
    context: event.context,
  };
}

function createConfig() {
  return {
    apiKey,
    enabledReleaseStages: null,
    autoDetectErrors: true,
    enabledErrorTypes: {
      unhandledExceptions: true,
      unhandledRejections: true,
    },
    plugins: isBrowser ? [new BugsnagPluginReact()] : [],
    onError(event: Event) {
      const logContext = getLogContext();

      if (shouldIgnoreError(event)) {
        if (isLocalLogging()) {
          console.log(
            `[Bugsnag ${logContext}] Error ignored (not sent):`,
            getEventLogPayload(event)
          );
        }
        return false;
      }

      if (apiKey === NOOP_BUGSNAG_API_KEY) {
        return false;
      }

      // eslint-disable-next-line no-param-reassign -- Bugsnag event mutation API
      event.app.releaseStage = getReleaseStage();

      if (isLocalLogging()) {
        console.log(`[Bugsnag ${logContext}] Sending error to Bugsnag:`, {
          releaseStage: event.app.releaseStage,
          ...getEventLogPayload(event),
        });
      }
    },
  };
}

export function startBugsnag() {
  if (isJestRunning() || apiKey === NOOP_BUGSNAG_API_KEY) {
    return noopBugsnag;
  }

  if (!Bugsnag.isStarted()) {
    return Bugsnag.start(createConfig());
  }

  return Bugsnag;
}

export const bugsnag = startBugsnag();

export default Bugsnag;
